import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/States";
import DatePicker from "@/components/DatePicker";
import MoneyInput from "@/components/MoneyInput";
import { TYPES } from "./meta";
import SupplierInput from "./SupplierInput";

const EMPTY = { type: "question", title: "", detail: "", assignee_id: "", due_date: "", shared: true, estimate_relevant: false, saving_amount: "", supplier: "", contact: "" };

export function PersonSelect({ people, value, onChange, testid }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-10 w-full rounded-md border bg-background px-3 text-sm" aria-label="Assigned to" data-testid={testid}>
      <option value="">Nobody yet</option>
      {people.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.role}</option>)}
    </select>
  );
}

export default function PrestartForm({ projectId, people, onAdded }) {
  const { user } = useAuth();
  const client = user.role === "client";
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const types = Object.entries(TYPES).filter(([, t]) => !client || t.client);
  const pickType = (type) => setF({ ...f, type, shared: client ? true : TYPES[type].shared, estimate_relevant: type === "saving" || type === "product" ? true : f.estimate_relevant });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { ...f, assignee_id: f.assignee_id || null, due_date: f.due_date || null, saving_amount: f.saving_amount === "" ? null : Number(f.saving_amount) };
      await api.post(`/projects/${projectId}/prestart`, body); setF(EMPTY); toast.success("Added to pre-start planning"); onAdded();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const T = TYPES[f.type];
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="prestart-form">
      <div>
        <Label className="text-xs text-muted-foreground">What kind of item?</Label>
        <div className="mt-1.5 flex flex-wrap gap-1.5" role="radiogroup" data-testid="prestart-type-picker">
          {types.map(([k, t]) => <button key={k} type="button" role="radio" aria-checked={f.type === k} onClick={() => pickType(k)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${f.type === k ? "border-primary bg-primary text-primary-foreground" : "hover:bg-secondary"}`} data-testid={`prestart-type-${k}`}><t.icon className="h-3 w-3" />{t.label}</button>)}
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">{T.hint}.</p>
      </div>
      <div className="space-y-1.5"><Label htmlFor="ps-title">Title</Label><Input id="ps-title" required maxLength={160} value={f.title} onChange={set("title")} placeholder={f.type === "saving" ? "e.g. Client's cousin supplies windows at trade price" : f.type === "question" ? "e.g. Which side should the sump discharge go?" : "Short and clear"} data-testid="prestart-title-input" /></div>
      <div className="space-y-1.5"><Label htmlFor="ps-detail">Details</Label><Textarea id="ps-detail" rows={3} maxLength={2000} value={f.detail} onChange={set("detail")} placeholder="Anything that helps whoever picks this up" data-testid="prestart-detail-input" /></div>
      {(f.type === "contact" || f.type === "saving" || f.type === "product") && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label htmlFor="ps-supplier">{f.type === "contact" ? "Company" : "Supplier / source"}</Label><SupplierInput id="ps-supplier" maxLength={160} value={f.supplier} onChange={(v) => setF((x) => ({ ...x, supplier: v }))} onPick={(c) => setF((x) => ({ ...x, supplier: c.company || c.name, contact: x.contact || c.contact || "" }))} testid="prestart-supplier-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="ps-contact">Contact (name, phone, email)</Label><Input id="ps-contact" maxLength={300} value={f.contact} onChange={set("contact")} data-testid="prestart-contact-input" /></div>
        </div>
      )}
      {!client && f.type === "saving" && (
        <div className="space-y-1.5"><Label htmlFor="ps-saving">Estimated saving ($, internal)</Label><MoneyInput id="ps-saving" value={f.saving_amount === "" ? null : f.saving_amount} onChange={(v) => setF((x) => ({ ...x, saving_amount: v ?? "" }))} className="w-40" data-testid="prestart-saving-input" /></div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Assigned to</Label><PersonSelect people={people} value={f.assignee_id} onChange={(v) => setF({ ...f, assignee_id: v })} testid="prestart-assignee-select" /></div>
        <div className="space-y-1.5"><Label>Due</Label><DatePicker value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })} placeholder="No date yet" testid="prestart-due-input" /></div>
      </div>
      {!client && (
        <div className="flex flex-wrap gap-6 rounded-lg border p-3">
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.shared} onCheckedChange={(v) => setF({ ...f, shared: v })} data-testid="prestart-shared-switch" />{f.shared ? "Shared with client" : "Internal only"}</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.estimate_relevant} onCheckedChange={(v) => setF({ ...f, estimate_relevant: v })} data-testid="prestart-estimate-switch" />Relevant to estimate</label>
        </div>
      )}
      <Button type="submit" className="w-full rounded-full" disabled={busy || !f.title.trim()} data-testid="prestart-submit-button">{busy ? <Spinner /> : "Add item"}</Button>
    </form>
  );
}
